import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useOutlet } from "@/lib/outlet";
import { toast } from "sonner";
import {
  AlertTriangle, Truck, FileText, Wallet, ChevronRight,
  Package, TrendingDown, AlertCircle, Zap,
} from "lucide-react";

/**
 * Action Deck — the new default landing view for Procurement.
 * Four cards + exceptions strip, all filtered by the active outlet.
 */
export default function ActionDeck({ onJumpTo }) {
  const { outletParam, outlet } = useOutlet();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/staff/procurement/action-deck", {
        params: outletParam ? { outlet: outletParam } : {},
      });
      setData(d);
    } catch {
      toast.error("Could not load Action Deck");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [outletParam]);

  const generateDraftPOs = async () => {
    if (!outletParam) {
      toast.error("Pick a specific outlet first — Generate draft POs is per-outlet.");
      return;
    }
    if (!window.confirm(`Generate draft POs from all approved indents for ${outlet}?`)) return;
    setGenerating(true);
    try {
      const { data: r } = await api.post("/staff/indents/convert-to-pos",
        { outlet: outletParam });
      if (r.pos_created === 0) {
        toast.info(r.reason || "No approved indents to convert");
      } else {
        toast.success(`Created ${r.pos_created} draft PO${r.pos_created === 1 ? "" : "s"}`);
        onJumpTo && onJumpTo("pos");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate POs");
    } finally { setGenerating(false); }
  };

  if (loading) return <div className="p-8 text-sm text-[hsl(var(--ink-soft))]">Loading…</div>;
  if (!data) return null;

  const cards = [
    {
      key: "below_par", icon: AlertTriangle, tone: "danger",
      title: "Below par", count: data.counts.below_par,
      sub: "SKUs at or under reorder point",
      action: { label: "Generate draft POs", onClick: generateDraftPOs, disabled: generating },
      viewAll: () => onJumpTo && onJumpTo("indents"),
    },
    {
      key: "awaiting_receipt", icon: Truck, tone: "amber",
      title: "Awaiting receipt", count: data.counts.awaiting_receipt,
      sub: "POs issued, goods pending",
      action: { label: "Open GRN queue", onClick: () => onJumpTo && onJumpTo("grns") },
    },
    {
      key: "awaiting_invoice", icon: FileText, tone: "brass",
      title: "Awaiting invoice", count: data.counts.awaiting_invoice,
      sub: "GRNs received, invoice not attached",
      action: { label: "Attach invoice", onClick: () => onJumpTo && onJumpTo("grns") },
    },
    {
      key: "payouts_due", icon: Wallet, tone: "forest",
      title: "Payouts due", count: data.counts.payouts_due,
      sub: "Vendors with outstanding balance",
      action: { label: "Open Payouts", onClick: () => onJumpTo && onJumpTo("payouts") },
    },
  ];

  return (
    <div data-testid="action-deck">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="eyebrow">Live queue</div>
          <h2 className="mt-1 display text-2xl">Action Deck <span className="text-sm text-[hsl(var(--ink-soft))] font-normal">· {outlet === "ALL" ? "All outlets" : outlet}</span></h2>
        </div>
        <button data-testid="action-deck-refresh" onClick={load}
                className="text-xs uppercase tracking-wider text-[hsl(var(--brass-2))] hover:underline">
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <ActionCard key={c.key} card={c} />
        ))}
      </div>

      {/* Exceptions strip */}
      <div className="mt-8">
        <div className="eyebrow mb-3">Exceptions <span className="text-[hsl(var(--ink-soft))]">· needs attention</span></div>
        {(data.exceptions || []).length === 0 ? (
          <div className="p-6 text-sm text-[hsl(var(--ink-soft))] border border-dashed border-[hsl(var(--rule))]">
            No exceptions right now. Nice.
          </div>
        ) : (
          <ul data-testid="action-deck-exceptions" className="divide-y divide-[hsl(var(--rule))] border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
            {(data.exceptions || []).map((ex, i) => (
              <li key={i} data-testid={`action-deck-exception-${i}`} className="p-3 text-sm flex items-center gap-3">
                <AlertCircle size={14} className="text-[hsl(6_62%_45%)] shrink-0" />
                <span className="flex-1">
                  {ex.kind === "missing_gstin" && (
                    <>Vendor <span className="font-semibold">{ex.vendor_name}</span> has no GSTIN on file.</>
                  )}
                  {ex.kind === "rate_variance" && (
                    <>PO <span className="font-mono">{ex.po_number}</span> — {ex.item_name} rate is <span className="font-semibold text-[hsl(6_62%_45%)]">+{ex.variance_pct}%</span> vs last purchase.</>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Detail lists for the two most operational cards */}
      {data.below_par.length > 0 && (
        <div className="mt-8">
          <div className="eyebrow mb-2">Below par · {data.below_par.length}</div>
          <div className="border border-[hsl(var(--rule))]">
            <table className="w-full text-xs">
              <thead className="bg-[hsl(var(--bg-2))] text-[hsl(var(--ink-soft))] uppercase tracking-wider">
                <tr><th className="text-left p-2">Item</th><th className="text-right p-2">Current</th><th className="text-right p-2">Reorder point</th><th className="text-right p-2">Reorder qty</th></tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--rule))]">
                {data.below_par.slice(0, 15).map((it) => (
                  <tr key={it.id} data-testid={`below-par-${it.id}`}>
                    <td className="p-2">{it.name}</td>
                    <td className="p-2 text-right text-[hsl(6_62%_45%)]">{it.current_stock} {it.unit}</td>
                    <td className="p-2 text-right">{it.reorder_point} {it.unit}</td>
                    <td className="p-2 text-right">{it.reorder_qty} {it.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionCard({ card }) {
  const { icon: Icon, tone } = card;
  const toneCls = {
    danger: "border-[hsl(6_62%_45%_/_0.4)] bg-[hsl(6_60%_98%)]",
    amber:  "border-[hsl(35_75%_38%_/_0.4)] bg-[hsl(38_90%_96%)]",
    brass:  "border-[hsl(var(--brass))]/40 bg-[hsl(var(--brass))]/8",
    forest: "border-[hsl(var(--forest))]/40 bg-[hsl(148_40%_96%)]",
  }[tone] || "border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]";
  const iconTone = {
    danger: "text-[hsl(6_62%_45%)]",
    amber: "text-[hsl(35_75%_38%)]",
    brass: "text-[hsl(var(--brass-2))]",
    forest: "text-[hsl(var(--forest))]",
  }[tone];
  return (
    <div data-testid={`action-card-${card.key}`} className={`p-4 border ${toneCls}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">{card.title}</div>
          <div className={`mt-1 display text-4xl ${iconTone}`}>{card.count}</div>
        </div>
        <Icon size={22} className={iconTone + " opacity-60"} />
      </div>
      <div className="mt-2 text-xs text-[hsl(var(--ink-soft))]">{card.sub}</div>
      {card.action && (
        <button
          data-testid={`action-card-${card.key}-cta`}
          onClick={card.action.onClick}
          disabled={card.action.disabled || card.count === 0}
          className="mt-3 w-full inline-flex items-center justify-between gap-2 text-xs uppercase tracking-wider px-3 py-2 border border-[hsl(var(--rule))] bg-[hsl(var(--bg))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] hover:border-[hsl(var(--forest))] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <span>{card.action.disabled ? "Working…" : card.action.label}</span>
          <ChevronRight size={12} />
        </button>
      )}
    </div>
  );
}
